import { createContext } from 'react';

import { AxiosResponse } from 'axios';

import {
  CompanyRolesResponseDTO,
  GetResourcePermissionOverridesParams,
  GetStaffResponse,
  ListStaffRequest,
  ModulePermissionOverridesDTO,
  PermissionCatalogueModuleDTO,
  PermissionOverridesPreviewPayload,
  ResourcePermissionOverridesResponseDTO,
  UpdateModulePermissionOverridePayload,
  UpdateResourcePermissionOverridePayload,
  UpdateStaffPermissionsPayload,
} from '@vyce/core/src/api/types';
import { FetchStaffModuleResources } from '@vyce/core/src/utils/staffPermissions';

// Every request is injected so the same module serves legend-app (/admin/v1) and
// pay-app (/public/v1).
interface Context {
  canEdit: boolean;
  getStaffRequest: (
    companyId: string,
    payload: ListStaffRequest
  ) => Promise<AxiosResponse<GetStaffResponse>>;
  listAvailableRolesRequest: (companyId: string) => Promise<AxiosResponse<CompanyRolesResponseDTO>>;
  updateStaffPermissionsRequest: (
    companyId: string,
    userId: string,
    data: UpdateStaffPermissionsPayload
  ) => Promise<AxiosResponse<any>>;
  getCompanyPermissionsCatalogueRequest: (
    companyId: string
  ) => Promise<AxiosResponse<PermissionCatalogueModuleDTO[]>>;
  getUserPermissionOverridesRequest: (
    companyId: string,
    userId: string
  ) => Promise<AxiosResponse<ModulePermissionOverridesDTO[]>>;
  updateUserPermissionOverridesRequest: (
    companyId: string,
    userId: string,
    payload: UpdateModulePermissionOverridePayload[]
  ) => Promise<AxiosResponse<ModulePermissionOverridesDTO[]>>;
  /**
   * The Add Admin dialog's Customise form for an email invite. Optional: only the
   * screens that host that dialog wire it, and without it an invite carries the
   * role defaults.
   */
  previewPermissionOverridesRequest?: (
    companyId: string,
    payload: PermissionOverridesPreviewPayload
  ) => Promise<AxiosResponse<ModulePermissionOverridesDTO[]>>;
  getUserResourcePermissionOverridesRequest: (
    companyId: string,
    userId: string,
    params: GetResourcePermissionOverridesParams
  ) => Promise<AxiosResponse<ResourcePermissionOverridesResponseDTO>>;
  updateResourcePermissionOverrideRequest: (
    companyId: string,
    userId: string,
    resourceId: string,
    payload: UpdateResourcePermissionOverridePayload
  ) => Promise<AxiosResponse<any>>;
  /**
   * The teams / locations / pay schedules inventory a resource-scoped module is
   * scoped to. Optional: pay-app gets the public/v1 default. Legend staff hold no
   * position in the customer's company, so the customer-facing pay look-up answers
   * them with nothing and the Payroll module would vanish — legend-app injects its
   * /admin/v1 loader here instead.
   */
  fetchModuleResources?: FetchStaffModuleResources;
}

export const RolesModuleContext = createContext({} as Context);
